/// <reference types="google.maps" />
import { useEffect, useRef, useState } from 'react';
import { GOOGLE_MAPS_LIBRARIES, getGoogleMapsApiKey } from '@/constants/googleMaps';

const GOOGLE_API_KEY = getGoogleMapsApiKey();

export interface PlaceDetails {
  address: string;
  latitude: number;
  longitude: number;
}

export const useGooglePlaces = () => {
  const [isLoaded, setIsLoaded] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const autocompleteServiceRef =
    useRef<google.maps.places.AutocompleteService | null>(null);
  const placesServiceRef =
    useRef<google.maps.places.PlacesService | null>(null);

  useEffect(() => {
    const initializeServices = () => {
      autocompleteServiceRef.current =
        new google.maps.places.AutocompleteService();
      placesServiceRef.current = new google.maps.places.PlacesService(
        document.createElement('div')
      );
      setIsLoaded(true);
    };

    // Already loaded?
    if (window.google?.maps?.places) {
      initializeServices();
      return;
    }

    // If script exists, wait for load
    const existingScript = document.querySelector(
      `script[src*="maps.googleapis.com"]`
    );
    if (existingScript) {
      const interval = setInterval(() => {
        if (window.google?.maps?.places) {
          initializeServices();
          clearInterval(interval);
        }
      }, 100);
      return () => clearInterval(interval);
    }

    // Validate API key
    if (!GOOGLE_API_KEY) {
      setError(
        'Google Maps API key is missing. Please add VITE_GOOGLE_MAPS_API_KEY in your .env file.'
      );
      return;
    }

    setIsLoading(true);

    // Load script - use same libraries as other components to prevent conflicts
    const librariesParam = GOOGLE_MAPS_LIBRARIES.join(',');
    const script = document.createElement('script');
    script.src = `https://maps.googleapis.com/maps/api/js?key=${GOOGLE_API_KEY}&libraries=${librariesParam}`;
    script.async = true;
    script.defer = true;

    script.onload = () => {
      if (window.google?.maps?.places) {
        initializeServices();
      } else {
        setError('Google Maps API loaded but "places" library missing.');
      }
      setIsLoading(false);
    };

    script.onerror = () => {
      setError('Failed to load Google Maps API.');
      setIsLoading(false);
    };

    document.head.appendChild(script);
  }, []);

  // -------------------------
  //   GET PLACE DETAILS
  // -------------------------
  const getPlaceDetails = (placeId: string): Promise<PlaceDetails> => {
    return new Promise((resolve, reject) => {
      if (!placesServiceRef.current) {
        reject(new Error('Places service not initialized yet.'));
        return;
      }

      placesServiceRef.current.getDetails(
        {
          placeId,
          fields: ['formatted_address', 'geometry', 'name'],
        },
        (place, status) => {
          if (status === google.maps.places.PlacesServiceStatus.OK && place) {
            const loc = place.geometry?.location;
            if (!loc) {
              reject(new Error('Location not found.'));
              return;
            }

            resolve({
              address: place.formatted_address || place.name || '',
              latitude: loc.lat(),
              longitude: loc.lng(),
            });
          } else {
            reject(new Error(`Failed: ${status}`));
          }
        }
      );
    });
  };

  // -------------------------
  // GET CURRENT LOCATION
  // -------------------------
  const getCurrentLocation = (): Promise<PlaceDetails> => {
    return new Promise((resolve, reject) => {
      if (!navigator.geolocation) {
        reject(new Error('Geolocation not supported.'));
        return;
      }

      navigator.geolocation.getCurrentPosition(
        (pos) => {
          resolve({
            address: 'Current Location',
            latitude: pos.coords.latitude,
            longitude: pos.coords.longitude,
          });
        },
        (err) => reject(new Error(`Geolocation error: ${err.message}`)),
        {
          enableHighAccuracy: true,
          timeout: 10000,
          maximumAge: 60000,
        }
      );
    });
  };

  // -------------------------
  // SEARCH AUTOCOMPLETE INPUT
  // -------------------------
  const searchPlaces = (
    input: string
  ): Promise<google.maps.places.AutocompletePrediction[]> => {
    return new Promise((resolve, reject) => {
      if (!autocompleteServiceRef.current) {
        reject(new Error('Autocomplete service not ready.'));
        return;
      }

      if (!input.trim()) {
        resolve([]);
        return;
      }

      const request = {
        input,
        types: ['geocode'], // <-- FIXED (no mixing)
      };

      autocompleteServiceRef.current.getPlacePredictions(
        request,
        (predictions, status) => {
          if (
            status === google.maps.places.PlacesServiceStatus.OK &&
            predictions
          ) {
            resolve(predictions);
          } else {
            resolve([]);
          }
        }
      );
    });
  };

  return {
    isLoaded,
    isLoading,
    error,

    autocompleteService: autocompleteServiceRef.current,
    placesService: placesServiceRef.current,

    getPlaceDetails,
    getCurrentLocation,
    searchPlaces,
  };
};


// /// <reference types="google.maps" />
// import { useEffect, useRef, useState } from 'react'

// const GOOGLE_API_KEY = import.meta.env.VITE_GOOGLE_MAPS_API_KEY || ''

// export const useGooglePlaces = () => {
//   const [isLoaded, setIsLoaded] = useState(false)
//   const [isLoading, setIsLoading] = useState(false)
//   const [error, setError] = useState<string | null>(null)
//   const autocompleteServiceRef =
//     useRef<google.maps.places.AutocompleteService | null>(null)
//   const placesServiceRef = useRef<google.maps.places.PlacesService | null>(null)

//   useEffect(() => {
//     // Check if Google Maps is already loaded
//     if (window.google && window.google.maps && window.google.maps.places) {
//       setIsLoaded(true)
//       autocompleteServiceRef.current =
//         new window.google.maps.places.AutocompleteService()
//       placesServiceRef.current = new window.google.maps.places.PlacesService(
//         document.createElement('div')
//       )
//       return
//     }

//     // Check if script is already being loaded
//     if (document.querySelector(`script[src*="maps.googleapis.com"]`)) {
//       // Wait for it to load
//       const checkLoaded = setInterval(() => {
//         if (window.google && window.google.maps && window.google.maps.places) {
//           setIsLoaded(true)
//           autocompleteServiceRef.current =
//             new window.google.maps.places.AutocompleteService()
//           placesServiceRef.current =
//             new window.google.maps.places.PlacesService(
//               document.createElement('div')
//             )
//           clearInterval(checkLoaded)
//         }
//       }, 100)
//       return () => clearInterval(checkLoaded)
//     }

//     // Validate API key
//     if (!GOOGLE_API_KEY) {
//       setError(
//         'Google Maps API key is not configured. Please set VITE_GOOGLE_MAPS_API_KEY in your .env file.'
//       )
//       setIsLoading(false)
//       return
//     }

//     // Load the script
//     setIsLoading(true)
//     const script = document.createElement('script')
//     script.src = `https://maps.googleapis.com/maps/api/js?key=${GOOGLE_API_KEY}&libraries=places`
//     script.async = true
//     script.defer = true

//     script.onload = () => {
//       if (window.google && window.google.maps && window.google.maps.places) {
//         setIsLoaded(true)
//         setIsLoading(false)
//         autocompleteServiceRef.current =
//           new window.google.maps.places.AutocompleteService()
//         placesServiceRef.current = new window.google.maps.places.PlacesService(
//           document.createElement('div')
//         )
//       } else {
//         setError('Google Maps API failed to load')
//         setIsLoading(false)
//       }
//     }

//     script.onerror = () => {
//       setError(
//         'Failed to load Google Maps API. Please check your API key and network connection.'
//       )
//       setIsLoading(false)
//     }

//     document.head.appendChild(script)

//     return () => {
//       // Don't remove script on unmount as it might be used by other components
//     }
//   }, [])

//   return {
//     isLoaded,
//     isLoading,
//     error,
//     autocompleteService: autocompleteServiceRef.current,
//     placesService: placesServiceRef.current,
//   }
// }
